/**
 * Image Settings Utilities
 * Manages dynamic image assignments from Firestore
 */

import { db } from '@/config/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { getStorageUrl } from '@/config/instructorImages';

export interface ImageSettings {
  instructorProfile: string;
  
  // Front Split (formerly Side Split)
  sideSplitSchedule: string;  // Field name kept for backwards compatibility
  sideSplitClasses: string;
  
  // Middle Split
  middleSplitSchedule: string;
  middleSplitClasses: string;
  
  // Handstands
  handstandsSchedule: string;
  handstandsClasses: string;
  
  // Back Flexibility
  backFlexibilitySchedule: string;
  backFlexibilityClasses: string;
  
  // Landing Page
  heroCard: string;
  heroBackground: string;
  aboutMeImage: string;
  gallery1: string;
  gallery2: string;
  gallery3: string;
  gallery4: string;
  gallery5: string;
  gallery6: string;
}

// Default fallbacks
const DEFAULT_SETTINGS: ImageSettings = {
  instructorProfile: 'flagpose_square.png',
  
  // Front Split (formerly Side Split)
  sideSplitSchedule: 'hanging-split.jpg',
  sideSplitClasses: 'hanging-split.jpg',
  
  // Middle Split
  middleSplitSchedule: 'squat-split.jpg',
  middleSplitClasses: 'squat-split.jpg',
  
  // Handstands
  handstandsSchedule: 'handstand.jpg',
  handstandsClasses: 'handstand.jpg',
  
  // Back Flexibility
  backFlexibilitySchedule: 'bridge.PNG',
  backFlexibilityClasses: 'bridge.PNG',
  
  // Landing Page
  heroCard: 'IMG_1459_Original2.jpg',
  heroBackground: 'IMG_3563 2.jpg',
  aboutMeImage: 'flagpose_square.png',
  gallery1: 'hanging-split.jpg',
  gallery2: 'squat-split.jpg',
  gallery3: 'handstand.jpg',
  gallery4: 'bridge.PNG',
  gallery5: 'flagpose_square.png',
  gallery6: 'hanging-split.jpg',
};

// Cache for settings
let cachedSettings: ImageSettings | null = null;
let lastFetch: number = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Get image settings from Firestore (with caching)
 */
export async function getImageSettings(): Promise<ImageSettings> {
  const now = Date.now();
  
  // Return cached if still valid
  if (cachedSettings && (now - lastFetch) < CACHE_DURATION) {
    return cachedSettings;
  }

  try {
    const settingsDoc = await getDoc(doc(db, 'settings', 'images'));
    
    if (settingsDoc.exists()) {
      cachedSettings = settingsDoc.data() as ImageSettings;
    } else {
      cachedSettings = DEFAULT_SETTINGS;
    }
    
    lastFetch = now;
    return cachedSettings;
  } catch (error) {
    console.error('Error fetching image settings:', error);
    return DEFAULT_SETTINGS;
  }
}

/**
 * Get instructor profile image URL
 */
export async function getInstructorProfileImage(): Promise<string> {
  const settings = await getImageSettings();
  return getStorageUrl(settings.instructorProfile);
}

/**
 * Get class type image URL
 */
export async function getClassTypeImage(
  classType: 'Front Split' | 'Middle Split' | 'Handstands' | 'Back Flexibility',
  page: 'schedule' | 'classes' = 'schedule'
): Promise<string> {
  const settings = await getImageSettings();
  
  const mapping: Record<string, { schedule: keyof ImageSettings; classes: keyof ImageSettings }> = {
    'Front Split': {
      schedule: 'sideSplitSchedule',  // Uses same field name for backwards compatibility
      classes: 'sideSplitClasses',
    },
    'Middle Split': {
      schedule: 'middleSplitSchedule',
      classes: 'middleSplitClasses',
    },
    'Handstands': {
      schedule: 'handstandsSchedule',
      classes: 'handstandsClasses',
    },
    'Back Flexibility': {
      schedule: 'backFlexibilitySchedule',
      classes: 'backFlexibilityClasses',
    },
  };
  
  const key = mapping[classType][page];
  return getStorageUrl(settings[key]);
}

/**
 * Clear cache (useful after updating settings)
 */
export function clearImageSettingsCache(): void {
  cachedSettings = null;
  lastFetch = 0;
}

/**
 * Get landing page hero card image URL
 */
export async function getHeroCardImage(): Promise<string> {
  const settings = await getImageSettings();
  return getStorageUrl(settings.heroCard);
}

/**
 * Get landing page hero background image URL
 */
export async function getHeroBackgroundImage(): Promise<string> {
  const settings = await getImageSettings();
  return getStorageUrl(settings.heroBackground);
}

/**
 * Get landing page About Me image URL
 */
export async function getAboutMeImage(): Promise<string> {
  const settings = await getImageSettings();
  return getStorageUrl(settings.aboutMeImage);
}

/**
 * Get landing page gallery images URLs
 */
export async function getGalleryImages(): Promise<string[]> {
  const settings = await getImageSettings();
  return [
    getStorageUrl(settings.gallery1),
    getStorageUrl(settings.gallery2),
    getStorageUrl(settings.gallery3),
    getStorageUrl(settings.gallery4),
    getStorageUrl(settings.gallery5),
    getStorageUrl(settings.gallery6),
  ];
}

/**
 * Get class card image by class name (for upcoming sessions)
 */
export async function getClassCardImageByClassName(
  className: 'Front Split' | 'Middle Split' | 'Handstands' | 'Back Flexibility'
): Promise<string> {
  return getClassTypeImage(className, 'schedule');
}

